import * as React from 'react'
import * as TestySDK from 'sdk/testy-web-sdk'
import * as T from 'components/ui/typography'

import { useRouter } from 'next/router'

import Button from 'components/shared/button'

import FormTextField from 'components/shared/form-field-input'
import SocialButton from 'components/shared/social-button'
import AppLogo from 'components/shared/logo'

import { IdentifierType } from 'pages/signup'
import Routes from 'utils/routes'
import useTranslation from 'next-translate/useTranslation'
import { useFormik } from 'formik'
import { SignUpPhoneRequiredSchema, UserInfoSchema } from 'utils/form/validation-schema'
import LinkButton from 'components/shared/link-button'
import { useAppConfig } from 'hooks/features/use-features'

interface UserInfoProps {
    onProceed: (identifier: string, identifierType: IdentifierType) => void
    setPhone: (val: string | undefined) => void
    setName: (val: string) => void
}

export default function UserInfo({ onProceed, setName, setPhone }: UserInfoProps) {
    const router = useRouter()
    const { t } = useTranslation('common')
    const { appConfig } = useAppConfig()

    const phoneRequired = appConfig.feature.includes('phoneRequired')

    const [loading, setLoading] = React.useState(false)

    const [identifierHelperText, setIdentifierHelperText] = React.useState('')

    const [nameHelperText, setNameHelperText] = React.useState('')

    const validationSchema = React.useMemo(() => {
        if (!phoneRequired) return UserInfoSchema
        else return SignUpPhoneRequiredSchema
    }, [phoneRequired])

    const form = useFormik({
        initialValues: {
            name: '',
            identifier: '',
            phone: '',
        },
        onSubmit: () => {
            onSubmit()
        },
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
    })

    const isPhoneIdentifier = React.useMemo(() => {
        const reg = new RegExp('^[+][0-9]{2}[0-9]{10}$') //* Da aggiungere numeri internazionali
        return reg.test(form.values.identifier.trim())
    }, [form.values.identifier])

    const isEmailIdentifier = React.useMemo(() => {
        const reg = new RegExp('[a-z0-9]+@[a-z]+.[a-z]{2,3}')
        return reg.test(form.values.identifier.trim())
    }, [form.values.identifier])

    const verifyUserWithEmailDoesNotExist = async () => {
        if (isEmailIdentifier)
            TestySDK.checkUserExistance({ email: form.values.identifier })
                .then(() => {
                    setIdentifierHelperText("C'è già un utente con questa email")
                })
                .catch((e) => {
                    if (e.response) {
                        if (e.response.status === 404) {
                            setName(form.values.name)
                            setPhone(form.values.phone)
                            onProceed(form.values.identifier, IdentifierType.Email)
                        } else setIdentifierHelperText(e.message)
                    } else {
                        setIdentifierHelperText(e.message)
                    }
                })
                .finally(() => setLoading(false))
        else {
            setIdentifierHelperText('Inserisci una mail valida!')
            setLoading(false)
            return
        }
    }

    const verifyUserWithPhoneDoesNotExist = async () => {
        TestySDK.checkUserExistance({ phone_number: form.values.identifier })
            .then(() => {
                setIdentifierHelperText("C'è già un utente con questo numero di telefono")
            })
            .catch((e) => {
                if (e.response) {
                    if (e.response.status === 404) {
                        setName(form.values.name)
                        onProceed(form.values.identifier, IdentifierType.Phone)
                    } else setIdentifierHelperText(e.message)
                } else {
                    setIdentifierHelperText(e.message)
                }
            })
            .finally(() => setLoading(false))
    }

    const onSubmit = async () => {
        setNameHelperText('')
        setIdentifierHelperText('')

        setLoading(true)

        if (isPhoneIdentifier) await verifyUserWithPhoneDoesNotExist()
        else await verifyUserWithEmailDoesNotExist()
    }

    const handleErrorIdentifier = React.useMemo(() => {
        if (form.errors.identifier) return t(`${form.errors.identifier}`)
        if (identifierHelperText) return identifierHelperText
        else return ''
    }, [form.errors.identifier, identifierHelperText, t])

    const handleErrorName = React.useMemo(() => {
        if (form.errors.name) return t(`${form.errors.name}`)
        if (nameHelperText) return nameHelperText
        else return ''
    }, [form.errors.name, nameHelperText, t])

    const handleErrorPhone = React.useMemo(() => {
        if (form.errors.phone) return t(`${form.errors.phone}`)
        else return ''
    }, [form.errors.phone, t])

    return (
        <div>
            <AppLogo />

            <T.H2 className="mt-3 mb-2 text-start">
                <>{t('hello')} 👋</>
            </T.H2>

            <T.H3 className="font-light mb-6 text-lg text-start">{t('welcomeNewUser')}</T.H3>

            <SocialButton social="Google" actionType="Signup" />
            {/* <SocialButton social="Apple" signMode="Sign" /> */}

            <T.Muted className="pt-3 pb-4">{t('signWith')}</T.Muted>

            <div id="recaptcha-container"></div>

            {/** Name field */}
            <FormTextField
                value={form.values.name}
                error={handleErrorName}
                onChangeFn={form.handleChange('name')}
                data-testid="name"
                placeholder={t('yourName')}
            />

            {/** Email/phone number field */}
            <FormTextField
                value={form.values.identifier}
                key="email"
                error={handleErrorIdentifier}
                type={'email'}
                data-testid="email"
                onChangeFn={form.handleChange('identifier')}
                placeholder={!phoneRequired ? t('yourIdentifier') : t('yourMail')}
            />

            {phoneRequired ? (
                <FormTextField
                    key="phone"
                    value={form.values.phone}
                    error={handleErrorPhone}
                    data-testid="phone"
                    onChangeFn={form.handleChange('phone')}
                    placeholder={t('phone')}
                />
            ) : null}

            <Button
                data-testid="signup-button"
                className="mt-3 w-full"
                disabled={!form.values.identifier && !form.values.name}
                loading={loading}
                onClick={() => form.handleSubmit()}>
                Continua
            </Button>

            <div className="flex items-center mt-2 ml-1 gap-2">
                <T.Muted className="text-neutral-600">{t('yesAccount')}</T.Muted>
                <LinkButton onClick={() => router.push(Routes.Login)} dataCy="change_mod_login">
                    {t('LogIn')}
                </LinkButton>
            </div>
        </div>
    )
}
